// store/sync.ts — Sync + Drive Backup state

import { create } from 'zustand';
import type { SyncLog, SyncStatus } from '../types';
import { getLatestSyncLog, getRecentSyncLogs, putSyncLog, exportAllData, getSettings, putSettings } from '../lib/db/queries';
import { nanoid } from '../lib/nanoid';
import { uploadBackup } from '../lib/sync/api-client';
import { restoreFromDrive as restoreFromDriveLib } from '../lib/sync/restore';
import { encryptData, sha256Hex } from '../lib/crypto';

import { auth } from '../lib/firebase';
import { getRemoteSyncLogs, disconnectDriveWithBackend } from '../lib/sync/api-client';


interface SyncStore {
  // Sync status
  status: SyncStatus | 'idle';
  lastSynced: string | null;
  lastBackupPath: string | null;
  recentLogs: SyncLog[];
  isConnected: boolean;
  isSyncing: boolean;

  // Restore status
  isRestoring: boolean;
  lastRestored: string | null;

  // Drive connection
  driveConnected: boolean;
  driveEmail: string | null;
  driveName: string | null;

  // Actions
  loadSyncStatus: (userId: string) => Promise<void>;
  connectDrive: (userId: string) => Promise<void>;
  disconnectDrive: (userId: string) => Promise<void>;
  triggerSync: (userId: string, masterKey?: CryptoKey | null) => Promise<void>;
  restoreFromDrive: (userId: string, masterKey: CryptoKey) => Promise<void>;
}

export const useSyncStore = create<SyncStore>((set, get) => ({
  status: 'idle',
  lastSynced: null,
  lastBackupPath: null,
  recentLogs: [],
  isConnected: false,
  isSyncing: false,

  isRestoring: false,
  lastRestored: null,

  // Initialise Drive state (will be populated reactively on loadSyncStatus)
  driveConnected: false,
  driveEmail: null,
  driveName: null,

  // -------------------------------------------------------------------------
  // loadSyncStatus
  // -------------------------------------------------------------------------

  loadSyncStatus: async (userId) => {
    const [latest, recent, settings] = await Promise.all([
      getLatestSyncLog(userId),
      getRecentSyncLogs(userId, 5),
      getSettings(userId),
    ]);

    const driveConnected = settings.driveConnected || false;
    const driveEmail = settings.driveEmail || null;
    const driveName = settings.driveName || null;

    set({
      status: latest?.status ?? 'idle',
      lastSynced: latest?.lastSyncedAt ?? null,
      recentLogs: recent,
      isConnected: latest?.status === 'success',
      driveConnected,
      driveEmail,
      driveName,
    });

    // Sync remote logs from the backend if Drive is connected
    if (driveConnected) {
      try {
        const remoteLogs = await getRemoteSyncLogs();
        if (remoteLogs && remoteLogs.length > 0) {
          for (const log of remoteLogs) {
            await putSyncLog(log);
          }
          // Reload updated status and logs
          const [updatedLatest, updatedRecent] = await Promise.all([
            getLatestSyncLog(userId),
            getRecentSyncLogs(userId, 5),
          ]);
          set({
            status: updatedLatest?.status ?? 'idle',
            lastSynced: updatedLatest?.lastSyncedAt ?? null,
            recentLogs: updatedRecent,
            isConnected: updatedLatest?.status === 'success',
          });
        }
      } catch (err) {
        console.warn('Failed to sync remote logs from backend:', err);
      }
    }
  },

  // -------------------------------------------------------------------------
  // connectDrive
  // -------------------------------------------------------------------------

  connectDrive: async (_userId: string) => {
    // 1. Get Firebase ID token
    const token = await auth.currentUser?.getIdToken();
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001';

    // 2. Redirect to backend OAuth login endpoint
    window.location.href = `${apiBaseUrl}/api/backup/auth/login?token=${token}`;
  },

  // -------------------------------------------------------------------------
  // disconnectDrive
  // -------------------------------------------------------------------------

  disconnectDrive: async (userId: string) => {
    await disconnectDriveWithBackend();

    // Remove connection info from settings
    const settings = await getSettings(userId);
    await putSettings({ ...settings, driveConnected: false, driveEmail: null, driveName: null });

    set({ driveConnected: false, driveEmail: null, driveName: null });
  },

  // -------------------------------------------------------------------------
  // triggerSync — export → encrypt → upload to Drive via backend
  // -------------------------------------------------------------------------

  triggerSync: async (userId, masterKey) => {
    set({ isSyncing: true, status: 'pending' });

    try {
      let fileId = '';
      let backupPath = '';
      let encryptedDataHash = '';
      const syncId = `sync_${nanoid()}`;

      if (get().driveConnected && masterKey) {
        // Export all IndexedDB data
        const rawData = await exportAllData(userId);
        const encryptedBlob = await encryptData(rawData, masterKey);
        encryptedDataHash = await sha256Hex(encryptedBlob);

        // Upload via backend (no driveToken parameter required)
        const result = await uploadBackup(encryptedBlob, encryptedDataHash, syncId);
        fileId = result.fileId;
        backupPath = result.backupPath;
      } else {
        // No Drive token or master key — fall back to simulated delay
        await new Promise(res => setTimeout(res, 1200));
      }

      const log: SyncLog = {
        syncId,
        userId,
        provider: 'google_drive',
        lastSyncedAt: new Date().toISOString(),
        encryptedDataHash,
        fileId,
        status: 'success',
        schemaVersion: 1,
      };

      await putSyncLog(log);
      set({
        status: 'success',
        lastSynced: log.lastSyncedAt,
        lastBackupPath: backupPath || get().lastBackupPath,
        isConnected: true,
        isSyncing: false,
        recentLogs: [log, ...get().recentLogs].slice(0, 5),
      });
    } catch (e) {
      const log: SyncLog = {
        syncId: `sync_${nanoid()}`,
        userId,
        provider: 'google_drive',
        lastSyncedAt: new Date().toISOString(),
        encryptedDataHash: '',
        fileId: '',
        status: 'error',
        errorMessage: String(e),
        schemaVersion: 1,
      };
      await putSyncLog(log);
      set({
        status: 'error',
        isSyncing: false,
        recentLogs: [log, ...get().recentLogs].slice(0, 5),
      });
      throw e;
    }
  },

  // -------------------------------------------------------------------------
  // restoreFromDrive — download backup → decrypt → import into IndexedDB
  // -------------------------------------------------------------------------

  restoreFromDrive: async (userId, masterKey) => {
    if (!get().driveConnected) return;

    set({ isRestoring: true });

    try {
      const { restored } = await restoreFromDriveLib(userId, masterKey);

      if (restored) {
        set({ isRestoring: false, lastRestored: new Date().toISOString() });
      } else {
        set({ isRestoring: false });
        throw new Error('No backup file found in Google Drive');
      }
    } catch (err) {
      console.error('[Restore] Restore failed:', (err as Error).message);
      set({ isRestoring: false });
      throw err;
    }
  },
}));
